import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, fileUrl } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-navy-900 placeholder:text-slate-400 focus:border-gold-dark focus:outline-none focus:ring-2 focus:ring-gold/30";

export async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await api.post("/upload", fd);
  return r.data.url;
}

function TextField({ field, value, onChange, testid }) {
  const type = field.type === "number" ? "number" : field.type === "date" ? "date" : "text";
  return <input id={testid} type={type} className={inputCls} value={value ?? ""} placeholder={field.placeholder || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
}

function TextareaField({ field, value, onChange, testid }) {
  return <textarea id={testid} rows={4} className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />;
}

function CheckboxField({ field, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#C5A059]" data-testid={testid} />
      {field.placeholder || "Enabled"}
    </label>
  );
}

function SelectField({ field, value, onChange, testid }) {
  return (
    <select id={testid} className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">Select…</option>
      {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function ImageField({ field, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      onChange(await uploadFile(f));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <input id={testid} className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload" data-testid={testid} />
        <label className="shrink-0 cursor-pointer rounded-lg bg-navy-950 px-4 py-2 text-xs font-semibold text-ivory hover:bg-navy-800" data-testid={`${testid}-upload`}>
          {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={handleUpload} />
        </label>
      </div>
      {value && <img src={fileUrl(value)} alt="Preview" className="h-20 rounded-lg object-cover" />}
    </div>
  );
}

const FIELD_COMPONENTS = {
  textarea: TextareaField,
  checkbox: CheckboxField,
  select: SelectField,
  image: ImageField,
};

export function FieldInput({ field, value, onChange }) {
  const Component = FIELD_COMPONENTS[field.type] || TextField;
  return <Component field={field} value={value} onChange={onChange} testid={`field-${field.key}`} />;
}

function initialValueFor(field) {
  if (field.type === "checkbox") return field.default ?? true;
  if (field.type === "number") return 0;
  return "";
}

function formValueFor(field, item) {
  const v = item[field.key];
  if (field.type === "list") return (v || []).join(", ");
  if (field.type === "checkbox") return v ?? false;
  return v ?? "";
}

function cellText(value) {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value ?? "—");
}

function ItemDialog({ open, onOpenChange, title, editing, fields, form, setForm, onSave, saving, testid }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid={`admin-${testid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-navy-900">
            {editing ? `Edit ${title.slice(0, -1)}` : `New ${title.slice(0, -1)}`}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2 sm:grid-cols-2">
          {fields.map((fl) => (
            <div key={fl.key} className={fl.type === "textarea" || fl.type === "image" || fl.full ? "sm:col-span-2" : ""}>
              <label htmlFor={`field-${fl.key}`} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                {fl.label}
              </label>
              <FieldInput field={fl} value={form[fl.key]} onChange={(v) => setForm((f) => ({ ...f, [fl.key]: v }))} />
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={() => onOpenChange(false)} className="btn-outline-dark !px-5 !py-2 !text-xs" data-testid={`admin-${testid}-cancel`}>
            <X className="h-3.5 w-3.5" aria-hidden /> Cancel
          </button>
          <button onClick={onSave} disabled={saving} className="btn-gold !px-5 !py-2 !text-xs disabled:opacity-60" data-testid={`admin-${testid}-save`}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ResourceTab({ resource, title, columns, fields, testid }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = () =>
    api.get(`/${resource}?admin=true`).then((r) => setItems(r.data)).catch(() => toast.error(`Failed to load ${title}`));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [resource]);

  const openNew = () => {
    const f = {};
    fields.forEach((fl) => { f[fl.key] = initialValueFor(fl); });
    setEditing(null);
    setForm(f);
    setOpen(true);
  };

  const openEdit = (item) => {
    const f = {};
    fields.forEach((fl) => { f[fl.key] = formValueFor(fl, item); });
    setEditing(item.id);
    setForm(f);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...form };
    fields.forEach((fl) => {
      if (fl.type === "list") payload[fl.key] = (payload[fl.key] || "").split(",").map((s) => s.trim()).filter(Boolean);
      if (fl.type === "number") payload[fl.key] = Number(payload[fl.key]) || 0;
    });
    try {
      if (editing) await api.put(`/${resource}/${editing}`, payload);
      else await api.post(`/${resource}`, payload);
      toast.success("Saved successfully");
      setOpen(false);
      load();
    } catch {
      toast.error("Could not save. Please check the fields.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item permanently?")) return;
    try {
      await api.delete(`/${resource}/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const visible = items.filter((it) =>
    !search || columns.some((c) => String(it[c.key] || "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div data-testid={`admin-${testid}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-serif text-2xl text-navy-900">{title}</h2>
        <div className="flex items-center gap-3">
          <input
            placeholder="Search…"
            className={`${inputCls} !w-48`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid={`admin-${testid}-search`}
          />
          <button onClick={openNew} className="btn-gold !px-5 !py-2.5 !text-xs" data-testid={`admin-${testid}-add`}>
            <Plus className="h-3.5 w-3.5" aria-hidden /> Add New
          </button>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid={`admin-${testid}-table`}>
        {visible.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-slate-500">No entries yet. Click “Add New” to create the first one.</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-ivory">
                {columns.map((c) => (
                  <th key={c.key} className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</th>
                ))}
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item, i) => (
                <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-ivory/60" data-testid={`admin-${testid}-row-${i}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="max-w-[220px] truncate px-5 py-3 text-slate-700">
                      {cellText(item[c.key])}
                    </td>
                  ))}
                  <td className="px-5 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(item)} aria-label="Edit" className="rounded-md border border-slate-200 p-1.5 text-slate-600 hover:border-gold-dark hover:text-gold-dark" data-testid={`admin-${testid}-edit-${i}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => remove(item.id)} aria-label="Delete" className="rounded-md border border-slate-200 p-1.5 text-slate-600 hover:border-red-400 hover:text-red-500" data-testid={`admin-${testid}-delete-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ItemDialog
        open={open}
        onOpenChange={setOpen}
        title={title}
        editing={editing}
        fields={fields}
        form={form}
        setForm={setForm}
        onSave={save}
        saving={saving}
        testid={testid}
      />
    </div>
  );
}
